import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

const VoiceContext = createContext(null);

export const useVoice = () => {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within VoiceProvider');
  }
  return context;
};

export const VoiceProvider = ({ children }) => {
  const { api, token } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchJobs = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await api.get('/voice/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch voice jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  const createJob = async (instanceId) => {
    const response = await api.post(`/voice/jobs?instance_id=${instanceId}`);
    await fetchJobs();
    return response.data;
  };

  const getJob = async (jobId) => {
    const response = await api.get(`/voice/jobs/${jobId}`);
    return response.data;
  };

  const validateDataset = async (jobId, samples) => {
    const response = await api.post(`/voice/jobs/${jobId}/validate-dataset`, samples);
    await fetchJobs();
    return response.data;
  };

  const configureTraining = async (jobId, config) => {
    const response = await api.post(`/voice/jobs/${jobId}/configure`, config);
    await fetchJobs();
    return response.data;
  };

  const generateNotebook = async (jobId, drivePath) => {
    const response = await api.post(`/voice/jobs/${jobId}/generate-notebook`, null, {
      params: { drive_path: drivePath }
    });
    await fetchJobs();
    return response.data;
  };

  const downloadNotebook = (jobId) => {
    const url = `${api.defaults.baseURL}/voice/jobs/${jobId}/notebook/download`;
    window.open(url, '_blank');
  };

  const importResults = async (jobId, checkpointPath, logs = []) => {
    const response = await api.post(`/voice/jobs/${jobId}/import-results`, {
      job_id: jobId,
      checkpoint_path: checkpointPath,
      logs
    });
    await fetchJobs();
    return response.data;
  };

  return (
    <VoiceContext.Provider
      value={{
        jobs,
        activeJob,
        setActiveJob,
        loading,
        fetchJobs,
        createJob,
        getJob,
        validateDataset,
        configureTraining,
        generateNotebook,
        downloadNotebook,
        importResults
      }}
    >
      {children}
    </VoiceContext.Provider>
  );
};
